import { BaseService } from './BaseService'
import {
  ScoutBrandsResponse,
  ScoutBrandResponse,
  ScoutBrandPostsResponse,
  ScoutBrandCategoriesResponse
} from '../types/scout'

export interface ContentPost {
  post_id: number | null       // scout post id — null for posts not yet ingested by scout
  social_post_id: number       // canonical social_posts id (always present)
  partition_key: number | null
  customer_id?: string
  platform_id: number
  platform_post_id: string | null
  post_url: string | null
  post_type: string | null
  caption: string | null
  thumbnail_blob_path: string | null
  content_category: string | null
  is_sponsored: boolean
  posted_at: string
  likes_count: number
  comments_count: number
  shares_count: number
  views_count: number
  emv: number
  account_username: string
  account_display_name: string | null
  collaborator_username?: string | null  // owner of a collab post owned by another account; null for own posts
  brand_count: number
}

export interface PostBrand {
  id: string
  brand_name: string
  total_detections: number
  last_seen_at: string | null
  post_detections: number
  confidence: number
  placement_tier: string | null
}

// Brand detected in the customer's OWNED/PARTNER content (dashboard "Brand Value" tab). No heat.
export interface OwnedBrand {
  id: string
  brand_name: string
  category: string | null
  filtered_detections: number
  filtered_posts: number
  avg_emv: number
  total_emv: number
  total_views: number
  total_likes: number
  total_comments: number
  total_shares: number
  account_usernames: string[]
  last_seen_at: string | null
}

export interface OwnedBrandsResponse {
  success: boolean
  data: OwnedBrand[]
  summary: { unique_brands: number; total_posts: number; total_emv: number; avg_emv: number }
  pagination: { currentPage: number; totalPages: number; totalItems: number; itemsPerPage: number; hasNextPage: boolean; hasPrevPage: boolean }
}

class ScoutService extends BaseService {
  constructor() {
    super('cro')
  }

  async getBrands(
    customerId: string,
    page?: number,
    limit?: number,
    sortBy?: string,
    order?: string,
    search?: string,
    accountIds?: string[],
    period?: string,
    customStartDate?: string,
    customEndDate?: string,
    categories?: string[]
  ): Promise<ScoutBrandsResponse> {
    return this.get<ScoutBrandsResponse>('/scout/brands', {
      customerId,
      page,
      limit,
      sortBy,
      order,
      search: search || undefined,
      accountIds: accountIds && accountIds.length > 0 ? accountIds.join(',') : undefined,
      period: period || undefined,
      customStartDate: customStartDate || undefined,
      customEndDate: customEndDate || undefined,
      categories: categories && categories.length > 0 ? categories.join(',') : undefined
    })
  }

  async getCategories(customerId: string): Promise<ScoutBrandCategoriesResponse> {
    return this.get<ScoutBrandCategoriesResponse>('/scout/brands/categories', { customerId })
  }

  async getBrandById(
    brandId: string,
    customerId: string
  ): Promise<ScoutBrandResponse> {
    return this.get<ScoutBrandResponse>(`/scout/brands/${brandId}`, {
      customerId
    })
  }

  async getContentPerformance(
    customerId: string,
    opts: { period?: string; customStartDate?: string; customEndDate?: string; accountIds?: string[]; scope?: 'owned' | 'all'; accountType?: 'owned' | 'partner' | 'all'; platformId?: number; platformIds?: number[] } = {}
  ): Promise<{ success: boolean; data: Array<{ content_category: string; post_type: string; post_count: number; total_emv: number }> }> {
    return this.get<{ success: boolean; data: Array<{ content_category: string; post_type: string; post_count: number; total_emv: number }> }>('/scout/brands/content-performance', {
      customerId,
      period: opts.period || undefined,
      customStartDate: opts.customStartDate || undefined,
      customEndDate: opts.customEndDate || undefined,
      accountIds: opts.accountIds && opts.accountIds.length > 0 ? opts.accountIds.join(',') : undefined,
      scope: opts.scope || undefined,
      accountType: opts.accountType || undefined,
      platformId: opts.platformId || undefined,
      platformIds: opts.platformIds && opts.platformIds.length > 0 ? opts.platformIds.join(',') : undefined,
    })
  }

  async getContentPerformancePosts(
    customerId: string,
    opts: { contentCategory: string; format: string; period?: string; customStartDate?: string; customEndDate?: string; accountIds?: string[]; scope?: 'owned' | 'all'; accountType?: 'owned' | 'partner' | 'all'; platformId?: number; platformIds?: number[] }
  ): Promise<{ success: boolean; data: ContentPost[] }> {
    return this.get<{ success: boolean; data: ContentPost[] }>('/scout/brands/content-performance/posts', {
      customerId,
      contentCategory: opts.contentCategory,
      format: opts.format,
      period: opts.period || undefined,
      customStartDate: opts.customStartDate || undefined,
      customEndDate: opts.customEndDate || undefined,
      accountIds: opts.accountIds && opts.accountIds.length > 0 ? opts.accountIds.join(',') : undefined,
      scope: opts.scope || undefined,
      accountType: opts.accountType || undefined,
      platformId: opts.platformId || undefined,
      platformIds: opts.platformIds && opts.platformIds.length > 0 ? opts.platformIds.join(',') : undefined,
    })
  }

  async getOwnedBrands(
    customerId: string,
    opts: { page?: number; limit?: number; sortBy?: string; order?: string; search?: string; accountIds?: string[]; accountType?: 'owned' | 'partner' | 'all'; platformIds?: number[]; period?: string; customStartDate?: string; customEndDate?: string; includeSelf?: boolean } = {}
  ): Promise<OwnedBrandsResponse> {
    return this.get<OwnedBrandsResponse>('/scout/brands/owned', {
      customerId,
      // Own brands are excluded from the rollup unless explicitly asked for.
      includeSelf: opts.includeSelf ? true : undefined,
      page: opts.page || undefined,
      limit: opts.limit || undefined,
      sortBy: opts.sortBy || undefined,
      order: opts.order || undefined,
      search: opts.search || undefined,
      accountIds: opts.accountIds && opts.accountIds.length > 0 ? opts.accountIds.join(',') : undefined,
      accountType: opts.accountType || undefined,
      platformIds: opts.platformIds && opts.platformIds.length > 0 ? opts.platformIds.join(',') : undefined,
      period: opts.period || undefined,
      customStartDate: opts.customStartDate || undefined,
      customEndDate: opts.customEndDate || undefined,
    })
  }

  async getOwnedBrandPosts(
    customerId: string,
    brandId: string,
    opts: { accountIds?: string[]; accountType?: 'owned' | 'partner' | 'all'; platformIds?: number[]; detectionTypes?: string[]; placementTiers?: string[]; contentCategories?: string[]; period?: string; customStartDate?: string; customEndDate?: string } = {}
  ): Promise<{ success: boolean; data: ContentPost[] }> {
    return this.get<{ success: boolean; data: ContentPost[] }>('/scout/brands/owned/posts', {
      customerId,
      brandId,
      accountIds: opts.accountIds && opts.accountIds.length > 0 ? opts.accountIds.join(',') : undefined,
      accountType: opts.accountType || undefined,
      platformIds: opts.platformIds && opts.platformIds.length > 0 ? opts.platformIds.join(',') : undefined,
      detectionType: opts.detectionTypes && opts.detectionTypes.length > 0 ? opts.detectionTypes.join(',') : undefined,
      placementTier: opts.placementTiers && opts.placementTiers.length > 0 ? opts.placementTiers.join(',') : undefined,
      contentCategory: opts.contentCategories && opts.contentCategories.length > 0 ? opts.contentCategories.join(',') : undefined,
      period: opts.period || undefined,
      customStartDate: opts.customStartDate || undefined,
      customEndDate: opts.customEndDate || undefined,
    })
  }

  async getPostBrands(customerId: string, postId: number): Promise<{ success: boolean; data: PostBrand[] }> {
    return this.get<{ success: boolean; data: PostBrand[] }>('/scout/brands/content-performance/post-brands', { customerId, postId })
  }

  getThumbnailUrl(postId: number | string, customerId: string): string {
    return `${this.baseURL}/scout/brands/thumbnails/${postId}?customerId=${customerId}`
  }

  async getBrandPosts(
    brandId: string,
    customerId: string,
    page?: number,
    limit?: number,
    accountIds?: string[],
    period?: string,
    customStartDate?: string,
    customEndDate?: string,
    detectionTypes?: string[],
    placementTiers?: string[],
    contentCategories?: string[]
  ): Promise<ScoutBrandPostsResponse> {
    return this.get<ScoutBrandPostsResponse>(`/scout/brands/${brandId}/posts`, {
      customerId,
      page,
      limit,
      ...(accountIds?.length ? { accountIds: accountIds.join(',') } : {}),
      ...(period ? { period } : {}),
      ...(customStartDate ? { customStartDate } : {}),
      ...(customEndDate ? { customEndDate } : {}),
      ...(detectionTypes?.length ? { detectionType: detectionTypes.join(',') } : {}),
      ...(placementTiers?.length ? { placementTier: placementTiers.join(',') } : {}),
      ...(contentCategories?.length ? { contentCategory: contentCategories.join(',') } : {}),
    })
  }
}

export const scoutService = new ScoutService()
export default scoutService
